import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import DraftBanner from "@/components/DraftBanner";
import PresetSelect from "@/components/PresetSelect";
import QuoteChecklist from "@/components/QuoteChecklist";
import { QUOTE_OPTION_DEFAULTS } from "@/lib/quoteOptions";
import { useDraftGuard } from "@/hooks/useDraftGuard";
import { FileDown, FileSpreadsheet, Loader2, Save, Wand2, Mail, Download, CheckCircle2, RefreshCw, Lock, FolderArchive, Paperclip, Copy } from "lucide-react";
import { toast } from "sonner";

const num = (v) => { const n = Number(String(v).replace(/,/g, "")); return v === "" || v == null || isNaN(n) ? 0 : n; };
const withCommas = (v) => {
  if (v === "" || v == null) return "";
  const [i, d] = String(v).split(".");
  const fi = i.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return d != null ? `${fi}.${d}` : fi;
};
const unitFromMargin = (cost, marginPct) => {
  const d = 1 - num(marginPct) / 100;
  return d > 0 ? num(cost) / d : num(cost);
};
const fmtDt = (iso) => { try { return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }); } catch { return iso || ""; } };

const DEFAULT_NOTES = "The prices quoted are valid only if this quotation is accepted in its entirety. Any partial acceptance, omission, modification, or change to the items, quantities, or terms may require the quotation to be re-priced. Lead times commence on receipt of an official purchase order (and cleared deposit where applicable). E&OE.";

export default function ClientQuotePanel({ rfqId, onChanged, onGotoCosting }) {
  const navigate = useNavigate();
  const [cq, setCq] = useState(null);
  const [rows, setRows] = useState([]);
  const [members, setMembers] = useState([]);
  const [qopts, setQopts] = useState(QUOTE_OPTION_DEFAULTS);
  const [revisions, setRevisions] = useState([]);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [downloading, setDownloading] = useState("");
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailForm, setEmailForm] = useState({ to: "", subject: "", message: "", attach_excel: false });
  const [sendingEmail, setSendingEmail] = useState(false);
  const [companyDocs, setCompanyDocs] = useState([]);
  const [oemLetters, setOemLetters] = useState([]);
  const [datasheetGroups, setDatasheetGroups] = useState([]);
  const [attachSel, setAttachSel] = useState({});
  const [supAttachSel, setSupAttachSel] = useState({});
  const [zipping, setZipping] = useState(false);

  const selectedDocIds = Object.keys(attachSel).filter((k) => attachSel[k]);
  const selectedSupDocIds = Object.keys(supAttachSel).filter((k) => supAttachSel[k]);
  const totalSel = selectedDocIds.length + selectedSupDocIds.length;
  const toggleDoc = (id) => setAttachSel((s) => ({ ...s, [id]: !s[id] }));
  const toggleSup = (id) => setSupAttachSel((s) => ({ ...s, [id]: !s[id] }));

  const loadAux = useCallback(() => {
    api.get(`/rfqs/${rfqId}/client-quote/revisions`).then((r) => setRevisions(r.data)).catch(() => {});
    api.get(`/rfqs/${rfqId}/quote-attachables`).then((r) => { setCompanyDocs(r.data.company_documents || []); setOemLetters(r.data.oem_letters || []); setDatasheetGroups(r.data.datasheets || []);
      const skuIds = (r.data.datasheets || []).flatMap((g) => g.files.map((f) => f.id)).filter((id) => id.startsWith("cd:"));
      setSupAttachSel((s) => ({ ...Object.fromEntries(skuIds.map((id) => [id, true])), ...s }));
    }).catch(() => {});
  }, [rfqId]);

  const load = useCallback(async () => {
    const { data } = await api.get(`/rfqs/${rfqId}/client-quote`);
    setCq(data);
    setRows((data.lines || []).map((l) => ({
      item_id: l.item_id, description: l.description, customer_requirement: l.customer_requirement || "", part_no: l.part_no ?? l.spec_or_part ?? "",
      brand_oem: l.brand_oem ?? "", quantity: l.quantity ?? 0, uom: l.uom || "EA",
      unit_price: Math.round(((l.client_unit_price ?? 0) - (l.delivery_unit || 0)) * 100) / 100, cost: l.cost ?? 0, margin_pct: l.margin_pct ?? 0,
      disc: l.disc ?? 0, lead_time: l.lead_time || "", supplier_id: l.supplier_id, supplier_name: l.supplier_name, supplier_currency: l.supplier_currency,
      supplier_lead_time: l.supplier_lead_time || "", ex_works_lead: l.ex_works_lead || "", transit_lead: l.transit_lead || "", coc_lead: l.coc_lead || "", cost_from_costing: !!l.cost_from_costing, from_stock: !!l.from_stock,
      extra_kind: l.extra_kind,
    })));
    loadAux();
  }, [rfqId, loadAux]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/team-members").then((r) => setMembers(r.data)).catch(() => {});
    api.get("/settings/quote-options").then((r) => setQopts(r.data)).catch(() => {});
  }, []);

  const setHeader = (patch) => setCq((c) => ({ ...c, ...patch }));
  const setRow = (id, patch) => setRows((rs) => rs.map((r) => (r.item_id === id ? { ...r, ...patch } : r)));
  const setLandedOrMargin = (id, patch) => setRows((rs) => rs.map((r) => {
    if (r.item_id !== id) return r;
    const next = { ...r, ...patch };
    next.unit_price = Math.round(unitFromMargin(next.cost, next.margin_pct) * 100) / 100;
    return next;
  }));

  const editable = cq ? {
    currency: cq.currency || "", default_markup_pct: cq.default_markup_pct, vat_rate: cq.vat_rate,
    vat_included: cq.vat_included, fx_rate_to_usd: cq.fx_rate_to_usd,
    quote_date: cq.quote_date || "", validity: cq.validity || "", incoterms: cq.incoterms || "",
    payment_terms: cq.payment_terms || "", notes: cq.notes || "", prepared_by_id: cq.prepared_by_id || "",
    delivery_to_site: cq.delivery_to_site || 0, delivery_label: cq.delivery_label || "", oem: cq.oem || null,
    rows: rows.map((r) => ({ item_id: r.item_id, unit_price: r.unit_price, quantity: r.quantity, part_no: r.part_no, brand_oem: r.brand_oem, uom: r.uom })),
  } : null;
  const guard = useDraftGuard(`client-quote:${rfqId}`, editable, { enabled: !!cq });
  const restoreDraft = () => {
    const d = guard.restore(); if (!d) return;
    const { rows: dr, ...header } = d;
    setCq((c) => ({ ...c, ...header }));
    if (dr) setRows((rs) => rs.map((r) => {
      const m = dr.find((x) => x.item_id === r.item_id);
      return m ? { ...r, ...m } : r;
    }));
  };

  const applyDefaultMargin = () => {
    const pct = num(cq.default_markup_pct);
    let skipped = 0;
    setRows((rs) => rs.map((r) => {
      if (num(r.cost) <= 0) { skipped += 1; return r; }
      return { ...r, unit_price: Math.round(unitFromMargin(r.cost, pct) * 100) / 100, margin_pct: pct };
    }));
    if (skipped) toast.warning(`Applied ${pct}% margin — ${skipped} line(s) skipped (no supplier cost; enter the unit price directly).`);
    else toast.success(`Applied ${pct}% margin to all unit prices`);
  };

  const buildPayload = () => ({
    currency: cq.currency || "", default_markup_pct: num(cq.default_markup_pct),
    vat_rate: num(cq.vat_rate), vat_included: !!cq.vat_included,
    fx_rate_to_usd: cq.fx_rate_to_usd != null && cq.fx_rate_to_usd !== "" ? num(cq.fx_rate_to_usd) : null,
    fx_include: !!cq.fx_include,
    quote_date: cq.quote_date || "", validity: cq.validity || "",
    incoterms: cq.incoterms || "", payment_terms: cq.payment_terms || "", notes: cq.notes || "",
    prepared_by_id: cq.prepared_by_id || "",
    delivery_to_site: num(cq.delivery_to_site), delivery_label: cq.delivery_label || "",
    oem: cq.oem || { enabled: false },
    show_specs: cq.show_specs !== false,
    contact_email: cq.contact_email ?? cq.client_email ?? "",
    lines: rows.map((r) => ({ item_id: r.item_id, description: r.description || "", unit_price: num(r.unit_price), quantity: num(r.quantity), part_no: r.part_no || "", brand_oem: r.brand_oem || "", uom: r.uom || "EA", lead_time: r.lead_time || "", cost: num(r.cost), margin_pct: num(r.margin_pct), disc: num(r.disc) })),
  });

  const save = async (silent) => {
    setSaving(true);
    try {
      await api.put(`/rfqs/${rfqId}/client-quote`, buildPayload());
      if (!silent) toast.success("Draft saved");
      guard.markSaved();
      await load();
      onChanged && onChanged();
    } catch (e) { toast.error(apiError(e)); throw e; } finally { setSaving(false); }
  };

  const blobDownload = (res, fallback) => {
    let filename = fallback;
    const cd = res.headers["content-disposition"] || res.headers["Content-Disposition"];
    const m = cd && cd.match(/filename="?([^"]+)"?/);
    if (m) filename = m[1];
    const url = window.URL.createObjectURL(new Blob([res.data]));
    const a = document.createElement("a");
    a.href = url; a.download = filename;
    document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
  };

  const download = async (format) => {
    setDownloading(format);
    try {
      await save(true);
      const res = await api.get(`/rfqs/${rfqId}/client-quote/export`, { params: { format }, responseType: "blob" });
      const ext = format === "excel" ? "xlsx" : "pdf";
      blobDownload(res, `${cq.quote_no || "SOM-QT"}.${ext}`);
    } catch (e) { toast.error(apiError(e)); } finally { setDownloading(""); }
  };

  const publish = async () => {
    setPublishing(true);
    try {
      await save(true);
      const { data } = await api.post(`/rfqs/${rfqId}/client-quote/publish`);
      toast.success(`Published ${data.label}`);
      const res = await api.get(`/rfqs/${rfqId}/client-quote/revisions/${data.id}/export`, { params: { format: "pdf" }, responseType: "blob" });
      blobDownload(res, `${data.label}.pdf`);
      loadAux();
      onChanged && onChanged();
    } catch (e) { toast.error(apiError(e)); } finally { setPublishing(false); }
  };

  const downloadRevision = async (rev, format) => {
    try {
      const res = await api.get(`/rfqs/${rfqId}/client-quote/revisions/${rev.id}/export`, { params: { format }, responseType: "blob" });
      blobDownload(res, `${rev.label}.${format === "excel" ? "xlsx" : "pdf"}`);
    } catch (e) { toast.error(apiError(e)); }
  };

  const downloadBidPack = async () => {
    setZipping(true);
    try {
      await save(true);
      const res = await api.get(`/rfqs/${rfqId}/client-quote/bid-pack`, {
        params: { format: "pdf", doc_ids: selectedDocIds.join(","), sup_doc_ids: selectedSupDocIds.join(",") }, responseType: "blob" });
      blobDownload(res, `${cq.quote_no || "SOM-QT"}_Bid_Pack.zip`);
    } catch (e) { toast.error(apiError(e)); } finally { setZipping(false); }
  };

  const openEmail = () => {
    const who = cq.client_attention || cq.client_name || "there";
    const title = cq.rfq_title || "";
    const ref = title || cq.client_ref || "";
    setEmailForm({
      to: cq.client_email || "",
      subject: `${cq.quote_no || "SOM-QT"} | Somcore Group Limited`,
      message: `Hi ${who},\n\nHope you are well.\n\nIn response to your valued enquiry${ref ? ` for ${ref}` : ""}, kindly find attached our offer${cq.quote_no ? ` (${cq.quote_no})` : ""} along with the supporting images and datasheets for your review and approval.\n\nThank you and please do let us know if you require anything further from our end and we will be happy to assist.`,
      attach_excel: false,
    });
    setEmailOpen(true);
  };
  const copyEmail = async () => {
    const text = `Subject: ${emailForm.subject}\n\n${emailForm.message}`;
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Email draft copied to clipboard");
      setEmailOpen(false);
    } catch (e) {
      toast.error("Couldn't copy — select the text manually");
    }
  };

  if (!cq) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  const cur = (cq.currency || "TZS").toUpperCase();
  const money = (v) => `${cur} ${num(v).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const delivery = num(cq.delivery_to_site);
  const baseTotal = rows.reduce((s, r) => s + num(r.unit_price) * num(r.quantity), 0);
  const share = (r) => (delivery > 0 && baseTotal && num(r.quantity) ? (delivery * num(r.unit_price) * num(r.quantity)) / baseTotal / num(r.quantity) : 0);
  const lineTotal = (r) => (num(r.unit_price) + share(r)) * num(r.quantity);
  const itemsTotal = rows.reduce((s, r) => s + lineTotal(r), 0);
  const vatRate = num(cq.vat_rate);
  const vat = cq.vat_included ? itemsTotal * vatRate : 0;
  const grand = itemsTotal + vat;
  const fx = (cq.fx_rate_to_usd != null && cq.fx_rate_to_usd !== "") ? num(cq.fx_rate_to_usd) : num(cq.fx_to_usd_live);
  const usdEquiv = fx ? grand * fx : 0;
  const perUsd = fx ? Math.round(1 / fx) : 0;

  const ro = (v) => <span className="text-slate-700">{v || "—"}</span>;
  const ckey = (c) => c.email || `name:${c.name}`;
  const pickContact = (k) => {
    const c = (cq.client_contacts || []).find((x) => ckey(x) === k);
    if (c) setHeader({ contact_email: c.email, client_attention: c.name, client_email: c.email, client_phone: c.phone || cq.client_phone });
  };
  const contactOpts = (cq.client_contacts || []).filter((c) => c.email || c.name);
  const curKey = cq.client_email || (cq.client_attention ? `name:${cq.client_attention}` : undefined);
  const contactSelect = contactOpts.length ? (
    <Select value={contactOpts.some((c) => ckey(c) === curKey) ? curKey : undefined} onValueChange={pickContact}>
      <SelectTrigger className="h-8 [&>span]:truncate" data-testid="cq-attention-select"><SelectValue placeholder={cq.client_attention || "Select contact"} /></SelectTrigger>
      <SelectContent>{contactOpts.map((c) => <SelectItem key={ckey(c)} value={ckey(c)}>{c.name || c.email}{c.title ? ` · ${c.title}` : ""}</SelectItem>)}</SelectContent>
    </Select>
  ) : ro(cq.client_attention);
  const metaRows = [
    [{ label: "Quote Reference", node: <span className="font-mono font-semibold text-[#1B4383]" data-testid="cq-quote-no">{cq.quote_no || "—"}{cq.quote_no && !cq.quote_no_locked ? <span className="ml-1.5 font-sans text-[10px] font-semibold uppercase text-amber-700">(Next · Locks On Publish)</span> : null}</span> },
     { label: "Date", node: <Input type="date" className="h-8" value={cq.quote_date || ""} onChange={(e) => setHeader({ quote_date: e.target.value })} data-testid="cq-date" /> }],
    [{ label: "Client Name", node: <span className="font-semibold text-[#1B2A4A]" data-testid="cq-client-name">{cq.client_name || "—"}</span> },
     { label: "Client RFQ Reference", node: ro(cq.client_ref) }],
    [{ label: "Attention", node: contactSelect },
     { label: "Currency", node: <PresetSelect options={qopts.currencies} value={cur} onChange={(v) => setHeader({ currency: v })} testid="cq-currency" /> }],
    [{ label: "Client Email", node: ro(cq.client_email) },
     { label: "Payment Terms", node: <PresetSelect options={qopts.payment_terms} value={cq.payment_terms || ""} onChange={(v) => setHeader({ payment_terms: v })} testid="cq-payment-terms" /> }],
    [{ label: "Client Phone", node: ro(cq.client_phone) },
     { label: "Incoterms", node: <PresetSelect options={qopts.incoterms} value={cq.incoterms || ""} onChange={(v) => setHeader({ incoterms: v })} testid="cq-incoterms" /> }],
    [{ label: "Client TIN / VRN", node: ro([cq.client_tin, cq.client_vrn].filter(Boolean).join(" / ")) },
     { label: "Quote Validity", node: <PresetSelect options={qopts.validity} value={cq.validity || ""} onChange={(v) => setHeader({ validity: v })} testid="cq-validity" /> }],
    [{ label: "Client Address", node: ro(cq.client_address) },
     { label: "Prepared By", node: <Select value={cq.prepared_by_id || "none"} onValueChange={(v) => setHeader({ prepared_by_id: v === "none" ? "" : v })}><SelectTrigger className="h-8" data-testid="cq-prepared-by"><SelectValue placeholder="Select" /></SelectTrigger><SelectContent><SelectItem value="none">—</SelectItem>{members.map((m) => (<SelectItem key={m.id} value={m.id}>{m.full_name || m.name}{m.job_title ? ` · ${m.job_title}` : ""}</SelectItem>))}</SelectContent></Select> }],
  ];

  return (
    <div className="space-y-4" style={{ fontFamily: "Calibri, Carlito, 'Segoe UI', sans-serif" }}>
      {guard.recovered && <DraftBanner onRestore={restoreDraft} onDiscard={guard.discard} testid="cq-draft-banner" />}

      {/* Action bar */}
      <div className="flex flex-wrap items-center justify-between gap-3" data-testid="cq-actionbar">
        <div className="flex items-center gap-2">
          <h2 className="font-heading text-base font-bold tracking-tight text-[#1B2A4A]">Somcore Quote</h2>
          {cq.quote_no && <span className="rounded-full bg-[#E6ECF5] px-2.5 py-0.5 text-xs font-medium text-[#1B4383]" data-testid="cq-quote-no">{cq.quote_no}{!cq.quote_no_locked ? " (Next)" : ""}</span>}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" size="default" onClick={openEmail} disabled={!rows.length} data-testid="cq-email"
            className="border-2 border-[#E0B84B] bg-[#FFFBEB] font-semibold text-[#8A6D1F] ring-2 ring-[#E0B84B]/50 ring-offset-1 hover:bg-[#FBF3D9]">
            <Mail className="mr-1.5 h-4 w-4" />Copy Email Draft
          </Button>
          <Button variant="outline" size="default" onClick={downloadBidPack} disabled={!rows.length || zipping} data-testid="cq-bid-pack">
            {zipping ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <FolderArchive className="mr-1.5 h-4 w-4" />}Bid Pack{totalSel ? ` (${totalSel})` : ""}
          </Button>
          <Button variant="outline" size="default" onClick={() => download("excel")} disabled={!rows.length || !!downloading} data-testid="cq-export-excel">
            {downloading === "excel" ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <FileSpreadsheet className="mr-1.5 h-4 w-4" />}Excel
          </Button>
          <Button variant="outline" size="default" onClick={() => save(false)} disabled={saving} data-testid="cq-save-draft">
            {saving ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Save className="mr-1.5 h-4 w-4" />}Save Draft
          </Button>
          <Button size="default" className="bg-[#1B4383] hover:bg-[#16386e]" onClick={publish} disabled={!rows.length || publishing} data-testid="cq-publish">
            {publishing ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <FileDown className="mr-1.5 h-4 w-4" />}Preview &amp; Publish PDF Quote
          </Button>
        </div>
      </div>

      <div className="grid items-start gap-4 xl:grid-cols-[minmax(0,1fr)_320px]">
      {/* Document paper */}
      <div className="min-w-0 overflow-hidden rounded-xl border border-border bg-white shadow-sm" data-testid="client-quote-panel">
        {/* Company header — mirrors the printed quote */}
        <div className={`flex items-center px-6 pt-6 ${cq.oem?.enabled ? "justify-between" : "justify-center"}`}>
          <img src={`${process.env.PUBLIC_URL || ""}/somcore-logo.png`} alt="Somcore" className="h-12 w-auto" data-testid="cq-logo" />
          {cq.oem?.enabled && (cq.oem.logo_b64 ? <img src={cq.oem.logo_b64} alt={cq.oem.name} className="max-h-14 max-w-[40%] object-contain" data-testid="cq-oem-logo" /> : <span className="font-heading text-lg font-bold text-[#1B4383]" data-testid="cq-oem-logo">{cq.oem.name}</span>)}
        </div>
        {cq.oem?.enabled && cq.oem.partner_line ? <div className="mt-2 text-center text-xs font-semibold text-[#1B4383]" data-testid="cq-oem-partner-band">{cq.oem.partner_line}</div> : null}
        <div className="mt-3 bg-[#1B4383] px-6 py-1.5 text-center text-[11px] text-white">
          450 Chole Road, Dar es Salaam, Tanzania &nbsp;|&nbsp; +255 687 719 909 &nbsp;|&nbsp; info@somcoregroup.com &nbsp;|&nbsp; TIN: 193-286-429 &nbsp;|&nbsp; VRN: 40-325861-W
        </div>
        <div className="flex items-center justify-center gap-3 bg-[#BFBFBF]/50 px-6 py-2 text-center">
          <span className="font-heading text-base font-bold tracking-wide text-[#1F2937]" data-testid="cq-title-band">QUOTATION{cq.oem?.enabled && cq.oem.quote_title ? ` – ${cq.oem.quote_title}` : ""}</span>
          {cq.quote_no ? <span className="font-mono text-sm font-semibold text-[#1B4383]" data-testid="cq-quote-no-band">{cq.quote_no}</span> : null}
        </div>

        <div className="space-y-6 p-6">
          {/* Quotation meta — mirrors the printed quote */}
          <div className="overflow-hidden rounded-lg border border-border" data-testid="cq-meta-grid">
            <table className="w-full text-sm">
              <tbody>
                {metaRows.map((mr, i) => (
                  <tr key={i} className="border-b border-border last:border-0">
                    <td className="w-[16%] bg-[#BFBFBF]/40 px-3 py-2 align-middle font-semibold text-[#1B2A4A]">{mr[0].label}</td>
                    <td className="w-[34%] px-3 py-1.5 align-middle">{mr[0].node}</td>
                    <td className="w-[16%] bg-[#BFBFBF]/40 px-3 py-2 align-middle font-semibold text-[#1B2A4A]">{mr[1].label}</td>
                    <td className="w-[34%] px-3 py-1.5 align-middle">{mr[1].node}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Line items */}
          <div>
            <div className="mb-2 flex items-center justify-between">
              <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Line Items ({rows.length})</div>
            </div>
            {!rows.length ? (
              <div className="rounded-lg border border-dashed border-border px-5 py-12 text-center text-sm text-muted-foreground" data-testid="cq-empty">
                No awarded items yet. Award a winning supplier per item in the <span className="font-medium">Compare</span> tab to build the quote.
              </div>
            ) : (
              <div className="overflow-x-auto rounded-lg border border-border">
                <table className="w-full table-fixed text-sm">
                  <colgroup>
                    <col style={{ width: "5%" }} />
                    <col style={{ width: "42%" }} />
                    <col style={{ width: "10%" }} />
                    <col style={{ width: "16%" }} />
                    <col style={{ width: "15%" }} />
                    <col style={{ width: "12%" }} />
                  </colgroup>
                  <thead className="bg-[#1B4383] text-left text-[11px] uppercase tracking-wide text-white">
                    <tr>
                      <th className="px-3 py-2.5 text-center font-semibold">S/N</th>
                      <th className="px-3 py-2.5 font-semibold">Item Description</th>
                      <th className="px-3 py-2.5 text-center font-semibold">Quantity</th>
                      <th className="px-3 py-2.5 text-right font-semibold">Unit Price ({cur})</th>
                      <th className="px-3 py-2.5 text-right font-semibold">Total Price ({cur})</th>
                      <th className="px-3 py-2.5 font-semibold">Lead Time</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r, idx) => (
                      <React.Fragment key={r.item_id}>
                      <tr className="border-t border-border align-top" data-testid={`cq-row-${r.item_id}`}>
                        <td className="px-3 py-2 text-center font-mono text-xs text-muted-foreground">{idx + 1}</td>
                        <td className="px-2 py-2">
                          {r.customer_requirement ? <div className="mb-1 text-[10px] leading-tight text-muted-foreground"><span className="font-semibold uppercase tracking-wide">Customer Requirement:</span> {r.customer_requirement}</div> : null}
                          <label className="mb-0.5 block text-[10px] font-semibold uppercase tracking-wide text-[#1B4383]">Somcore Description</label>
                          <Input className="h-8 w-full" value={r.description || ""} onChange={(e) => setRow(r.item_id, { description: e.target.value })} data-testid={`cq-desc-${r.item_id}`} />
                          {r.extra_kind ? <span className="mt-0.5 inline-block rounded bg-[#E4EEE8] px-1.5 py-0.5 text-[10px] font-medium text-[#527D63]">{r.extra_kind}</span> : null}
                        </td>
                        <td className="px-2 py-2 text-center"><Input type="number" className="h-8 w-full text-center font-mono" value={r.quantity ?? ""} onChange={(e) => setRow(r.item_id, { quantity: e.target.value })} data-testid={`cq-qty-${r.item_id}`} /></td>
                        <td className="px-2 py-2 text-right"><Input inputMode="decimal" className="h-8 w-full text-right font-mono" value={withCommas(r.unit_price)} onChange={(e) => setRow(r.item_id, { unit_price: e.target.value.replace(/,/g, "") })} data-testid={`cq-unit-${r.item_id}`} />{share(r) > 0 && <div className="mt-0.5 text-[10px] text-[#527D63]" data-testid={`cq-delivery-share-${r.item_id}`}>+ {withCommas(Math.round(share(r) * 100) / 100)} Delivery</div>}</td>
                        <td className="px-3 py-2 text-right font-mono font-semibold text-slate-900" data-testid={`cq-total-${r.item_id}`}>{money(lineTotal(r))}</td>
                        <td className="px-2 py-2"><Input className="h-8 w-full text-xs" placeholder="Ex Stock" value={r.lead_time || ""} onChange={(e) => setRow(r.item_id, { lead_time: e.target.value })} data-testid={`cq-lead-${r.item_id}`} /></td>
                      </tr>
                      <tr className="border-t border-[#DCE4F0] bg-[#F1F5FB]" data-testid={`cq-internal-${r.item_id}`}>
                        <td></td>
                        <td colSpan={5} className="px-3 py-2">
                          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-[#1B2A4A]">
                            <span className="inline-flex items-center gap-1 font-semibold uppercase tracking-wide text-[#1B4383]"><Lock className="h-3 w-3" />Internal · hidden from client</span>
                            <span>OEM / Supplier: <span className="font-medium">{r.supplier_name || "—"}</span></span>
                            <span data-testid={`cq-exw-lead-${r.item_id}`}>Ex Works Lead Time: <span className="font-medium">{r.ex_works_lead || "—"}</span></span>
                            <span data-testid={`cq-transit-lead-${r.item_id}`}>Transit Lead Time: <span className="font-medium">{r.transit_lead || "—"}</span></span>
                            <span data-testid={`cq-coc-lead-${r.item_id}`}>COC Lead Time: <span className="font-medium">{r.coc_lead || "—"}</span></span>
                            <label className="flex items-center gap-1.5">Landed / Unit
                              {r.cost_from_costing ? (
                                <span className="inline-flex h-7 w-32 items-center justify-end rounded-md border border-slate-200 bg-white px-2 font-mono text-slate-700" title={r.from_stock ? "Landed cost entered on the SKU in the catalog" : "Pulled from the supplier's Confirmed Quote Basis costing"} data-testid={`cq-landed-${r.item_id}`}>{withCommas(r.cost)}</span>
                              ) : (
                                <Input inputMode="decimal" className="h-7 w-32 text-right font-mono" value={withCommas(r.cost)} onChange={(e) => setLandedOrMargin(r.item_id, { cost: e.target.value.replace(/,/g, "") })} data-testid={`cq-landed-${r.item_id}`} />
                              )}</label>
                            {r.supplier_id ? <button type="button" onClick={() => navigate(`/rfqs/${rfqId}/quotes/${r.supplier_id}`)} className="text-[#1B4383] underline underline-offset-2 hover:text-[#16386e]" data-testid={`cq-refer-costing-${r.item_id}`}>Refer to {r.supplier_name || "supplier"} Costing →</button> : null}
                            <label className="flex items-center gap-1.5">Margin %
                              <Input type="number" className="h-7 w-20 text-right font-mono" value={r.margin_pct ?? ""} onChange={(e) => setLandedOrMargin(r.item_id, { margin_pct: e.target.value })} data-testid={`cq-margin-${r.item_id}`} /></label>
                          </div>
                        </td>
                      </tr>
                      </React.Fragment>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="border-t-2 border-border bg-slate-50">
                      <td colSpan={4} className="px-3 py-2.5 text-right text-xs font-semibold uppercase tracking-wide text-slate-600">Subtotal (Excl. VAT)</td>
                      <td className="px-3 py-2.5 text-right font-mono font-bold text-slate-900" data-testid="cq-items-total">{money(itemsTotal)}</td>
                      <td></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </div>

          {/* Totals */}
          <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
            <div className="w-full max-w-md rounded-lg border border-dashed border-[#B9CBE4] bg-[#F7FAFE] p-3" data-testid="cq-delivery-box">
              <div className="text-xs font-bold uppercase tracking-wide text-[#1B4383]">Delivery To Site <span className="font-normal normal-case text-muted-foreground">· Internal, Combined For All Suppliers</span></div>
              <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2">
                <label className="text-xs text-slate-600">Total Transport ({cur})<Input inputMode="decimal" className="mt-1 h-8 text-right font-mono" value={withCommas(cq.delivery_to_site || "")} onChange={(e) => setHeader({ delivery_to_site: e.target.value.replace(/,/g, "") })} placeholder="0" data-testid="cq-delivery-amount" /></label>
                <label className="text-xs text-slate-600">Site / Note<Input className="mt-1 h-8" value={cq.delivery_label || ""} onChange={(e) => setHeader({ delivery_label: e.target.value })} placeholder="e.g. Geita, 1 × Truck" data-testid="cq-delivery-label" /></label>
              </div>
              <p className="mt-2 text-[11px] text-muted-foreground">Spread Into The Line Prices By Value At Cost (No Margin). The Client Sees No Separate Transport Line. Leave Each Supplier's Transport & Labour At 0 So It Isn't Counted Twice.</p>
            </div>
          <div className="flex justify-end md:flex-1">
            <div className="w-full max-w-sm space-y-2">
              <div className="flex items-center justify-between text-sm"><span className="text-muted-foreground">Subtotal (Excl. VAT)</span><span className="font-mono font-semibold">{money(itemsTotal)}</span></div>
              <div className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-2">
                  <span className="text-muted-foreground">VAT</span>
                  <Input type="number" min="0" max="100" step="0.01" className="h-7 w-16 text-right font-mono" value={cq.vat_rate != null ? Math.round(num(cq.vat_rate) * 10000) / 100 : ""} onChange={(e) => setHeader({ vat_rate: num(e.target.value) / 100 })} data-testid="cq-vat-rate" />
                  <span className="text-muted-foreground">%</span>
                  <label className="flex items-center gap-1.5 text-xs"><Checkbox checked={!!cq.vat_included} onCheckedChange={(v) => setHeader({ vat_included: !!v })} data-testid="cq-vat-include" />include</label>
                </div>
                <span className="font-mono">{money(vat)}</span>
              </div>
              <div className="flex items-center justify-between rounded-md bg-[#0F172A] px-4 py-2.5 text-white">
                <span className="text-sm font-bold">TOTAL (Incl. VAT)</span>
                <span className="font-mono text-base font-bold" data-testid="cq-grand-total">{money(grand)}</span>
              </div>
              {cur !== "USD" ? (
                <div className="mt-1 space-y-1.5 rounded-md border border-border bg-slate-50 p-2.5" data-testid="cq-fx-block">
                  <div className="flex items-center justify-between text-sm">
                    <span className="font-medium text-[#1B2A4A]">USD Equivalent:</span>
                    <span className="font-mono font-semibold" data-testid="cq-fx-usd">USD {usdEquiv.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                  </div>
                  <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground">
                    <label className="flex items-center gap-1.5">Rate
                      <Input type="number" className="h-7 w-24 text-right font-mono" value={perUsd || ""} onChange={(e) => { const v = num(e.target.value); setHeader({ fx_rate_to_usd: v > 0 ? 1 / v : null }); }} data-testid="cq-fx-rate" />
                      <span>{cur}/USD</span>
                    </label>
                    <label className="flex items-center gap-1.5"><Checkbox checked={!!cq.fx_include} onCheckedChange={(v) => setHeader({ fx_include: !!v })} data-testid="cq-fx-include" />Show on quote</label>
                  </div>
                </div>
              ) : null}
            </div>
          </div>
          </div>

          {/* Prepared by is in the meta grid above */}

          {/* Read-only preview of what prints below the table */}
          <div className="space-y-3">
            <div className="overflow-hidden rounded-lg border border-border">
              <div className="bg-[#1B4383] px-3 py-1.5 text-xs font-bold uppercase tracking-wide text-white">Terms &amp; Conditions</div>
              <div className="p-3 text-[11px] leading-relaxed text-muted-foreground">
                {cq.oem?.enabled ? `Standard Somcore terms print, with ${(cq.oem.terms || []).length} ${cq.oem.name || "OEM"} clause(s) replacing or added to them (see the Quote Checklist).` : "Standard Somcore terms print automatically (Prices, Delivery Period, Point of Delivery, Payment Terms, Validity, Non-Conformance, Force Majeure)."}
              </div>
            </div>
            <div className="overflow-hidden rounded-lg border border-border">
              <div className="bg-[#1B4383] px-3 py-1.5 text-xs font-bold uppercase tracking-wide text-white">Our Bank Details</div>
              <div className="p-3 text-[11px] leading-relaxed text-muted-foreground">
                DTB Tanzania — A/C TZS 0356505001 · USD 0356505002 · SWIFT DTKETZTZ · Branch Code 001<br />
                Stanbic Bank (T) — A/C TZS 9120003754020 · USD 9120003754187 · SWIFT SBICTZTX · Sort Code 006000
              </div>
            </div>
            <p className="text-[11px] text-muted-foreground">Logo, terms and bank details print on the PDF and are managed in Settings.</p>
          </div>
        </div>
      </div>
      <QuoteChecklist cq={cq} setHeader={setHeader} awardedSupplierIds={rows.map((r) => r.supplier_id).filter(Boolean)} defaultNotes={DEFAULT_NOTES} totalSel={totalSel}
        rfqId={rfqId} splitLines={rows.map((r) => ({ item_id: r.item_id, description: r.description, line_total: lineTotal(r) }))} onSplitSaved={async () => { await save(true); await load(); }}
        companyDocs={companyDocs} oemLetters={oemLetters} datasheetGroups={datasheetGroups} attachSel={attachSel} supAttachSel={supAttachSel} toggleDoc={toggleDoc} toggleSup={toggleSup} />
      </div>

      {/* Revision history */}
      <div className="rounded-lg border border-border bg-white p-5" data-testid="cq-revisions">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="flex items-center gap-2 font-heading text-sm font-bold tracking-tight"><CheckCircle2 className="h-4 w-4 text-[#527D63]" />Revision History ({revisions.length})</h3>
          <Button variant="ghost" size="sm" className="h-7" onClick={loadAux} data-testid="cq-rev-refresh"><RefreshCw className="mr-1 h-3.5 w-3.5" />Refresh</Button>
        </div>
        {revisions.length ? (
          <div className="space-y-2">
            {revisions.map((v) => (
              <div key={v.id} className="flex items-center justify-between gap-3 rounded-md border border-border p-2.5" data-testid={`cq-rev-${v.id}`}>
                <div className="min-w-0">
                  <div className="flex items-center gap-2 font-medium text-slate-800">{v.label}
                    {v.published ? <span className="rounded-full bg-[#E4EEE8] px-2 py-0.5 text-[10px] font-semibold text-[#527D63]">Published</span> : null}</div>
                  <div className="truncate text-xs text-muted-foreground">{fmtDt(v.created_at)} · {v.items} items · {v.currency} {Number(v.total).toLocaleString()} · {v.created_by_name || "—"}</div>
                </div>
                <div className="flex shrink-0 gap-1.5">
                  <Button variant="outline" size="sm" className="h-7 px-2" onClick={() => downloadRevision(v, "pdf")} data-testid={`cq-rev-pdf-${v.id}`}><Download className="mr-1 h-3.5 w-3.5" />PDF</Button>
                  <Button variant="outline" size="sm" className="h-7 px-2" onClick={() => downloadRevision(v, "excel")} data-testid={`cq-rev-excel-${v.id}`}><Download className="mr-1 h-3.5 w-3.5" />Excel</Button>
                </div>
              </div>
            ))}
          </div>
        ) : <p className="text-xs text-muted-foreground" data-testid="cq-rev-empty">No published revisions yet — each time you Publish, a snapshot (data + exact PDF) is kept here for comparison.</p>}
      </div>

      <Dialog open={emailOpen} onOpenChange={setEmailOpen}>
        <DialogContent data-testid="cq-email-dialog">
          <DialogHeader><DialogTitle>Copy Email Draft</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5"><Label className="text-xs">Subject</Label>
              <Input value={emailForm.subject} onChange={(e) => setEmailForm({ ...emailForm, subject: e.target.value })} data-testid="email-subject" /></div>
            <div className="space-y-1.5"><Label className="text-xs">Message</Label>
              <Textarea rows={9} value={emailForm.message} onChange={(e) => setEmailForm({ ...emailForm, message: e.target.value })} data-testid="email-message" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEmailOpen(false)}>Cancel</Button>
            <Button onClick={copyEmail} data-testid="email-copy">
              <Copy className="mr-2 h-4 w-4" />Copy Email Draft
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
